/**
 * The shorter terms a single multi-year STEPPED agreement also offers.
 *
 * Origin's NYM NOOSA schedule is one 51-month agreement (01/10/2026 → 31/12/2030) listing five
 * Agreement Years. The client's model does not compare it at 51 months only — it ranks Origin
 * in the 12, 24, 36 and 48-month tables too, each costed as the agreement years up to that
 * point. Their 24-Month table reads Origin at 34,286, which is Agreement Year 1 + Agreement
 * Year 2, and prints 10.4346 — Agreement Year 2's rate, the last year of that term.
 *
 * So one 5-year stepped agreement offers FIVE terms:
 *
 *   12mo = AY1              24mo = AY1+AY2         36mo = AY1..AY3
 *   48mo = AY1..AY4         51mo = AY1..AY5        (the full span, 3-month tail included)
 *
 * This is derived in code rather than asked of the parser on purpose. The parser's job is to
 * transcribe the document faithfully — ONE term with year_1..year_N — and it was asking it to
 * invent 12/24/36/48 that produced the bug this rescues: four fabricated terms each holding a
 * single agreement year in its own year_1, every later slot left empty, and Agreement Year 5
 * discarded because the prompt's fallback list stopped at Year 4.
 *
 * Deliberately narrow. Expansion happens ONLY when the offer quotes exactly one term length,
 * so an offer that already lists its own 12/24/36/48 options is never touched — measured on
 * prod, 26 offers across 16 tenders and 3 retailers gain sub-terms and 2,366 are untouched.
 */

import { seriesMonthsForYear, type TenderTermSeries } from './tenderTermSeries';
import { inclusiveMonthSpan } from './termDateRanges';

/** `contract_terms`, keyed "51", "51_stepped" or "51_smooth". */
export type ContractTermsMap = Record<string, any>;

const MONTHS_IN_YEAR = 12;

/** Months a term key names, and the suffix to reuse for derived keys. */
const parseTermKey = (key: string): { months: number; suffix: string } | null => {
    const match = String(key).match(/^(\d+)(.*)$/);
    if (!match) return null;
    const months = parseInt(match[1], 10);
    if (!Number.isFinite(months) || months <= 0) return null;
    return { months, suffix: match[2] ?? '' };
};

/** The year_N slot numbers a term carries, ascending. */
const yearNumbersOf = (term: Record<string, any>): number[] =>
    Object.keys(term)
        .map(k => k.match(/^year_(\d+)$/)?.[1])
        .filter((n): n is string => !!n)
        .map(Number)
        .sort((a, b) => a - b);

/** Does this year slot carry usable rates? An empty `{}` does not. */
const isPricedYear = (slot: unknown): boolean => {
    if (!slot || typeof slot !== 'object') return false;
    const peak = Number((slot as Record<string, unknown>).peak);
    return Number.isFinite(peak) && peak > 0;
};

/**
 * How many leading years of this term are priced, without a gap.
 *
 * Only a CONSECUTIVE run counts: a term whose year_4 is empty can still be offered at 12, 24
 * and 36 months, but nothing beyond the gap can be costed. Origin's other prod offers lose
 * exactly the short final agreement year, and this salvages every term before it.
 */
const pricedYearRun = (term: Record<string, any>, years: number[]): number => {
    let run = 0;
    for (const n of years) {
        if (n !== run + 1) break;              // a missing slot number ends the run
        if (!isPricedYear(term[`year_${n}`])) break;
        run = n;
    }
    return run;
};

/**
 * One agreement's YEARS mis-parsed as several separate term OPTIONS.
 *
 * Origin's NYM NOOSA schedule is a single 51-month agreement listing five Agreement Years. The
 * parser emitted four terms — 12/24/36/48 — each holding ONE agreement year's rates in its own
 * `year_1` with every later slot left as `{}`, and it got `available_terms: ["51"]` right while
 * doing so. A stepped term cannot be costed with an empty year, so 24/36/48 were dropped and
 * Origin ranked in the Year 1 table only.
 *
 * Returns the pieces needed to rebuild the real term, or null when this is not that shape.
 *
 * `available_terms` is the decisive signal, because rate VALUES cannot separate the two cases —
 * a genuine set of term options also quotes a different rate per option. Only the document's own
 * declared term length being absent from every key gives it away.
 *
 * Every one of these must hold: two or more terms, all stepped, each pricing only its `year_1`
 * while carrying at least one later empty slot, all those `year_1` rates distinct, and exactly
 * one declared term length missing from the keys.
 */
export function detectSplitAgreementYears(
    contractTerms: ContractTermsMap | null | undefined,
    availableTerms: unknown,
): { declaredMonths: number; suffix: string; yearsInOrder: any[] } | null {
    if (!contractTerms || typeof contractTerms !== 'object') return null;

    const parsed = Object.entries(contractTerms)
        .map(([key, value]) => ({ key, value, meta: parseTermKey(key) }))
        .filter((e): e is { key: string; value: any; meta: { months: number; suffix: string } } =>
            e.meta !== null && !!e.value && typeof e.value === 'object')
        .sort((a, b) => a.meta.months - b.meta.months);

    if (parsed.length < 2) return null;

    let sawLaterEmptySlot = false;
    const yearsInOrder: any[] = [];
    const peaks: number[] = [];

    for (const { value: term } of parsed) {
        if (term.rate_type !== 'Stepped') return null;

        const years = yearNumbersOf(term);
        if (years.length === 0 || years[0] !== 1) return null;
        if (!isPricedYear(term.year_1)) return null;
        // A priced year beyond the first means these really are per-year rates, not a split.
        if (years.slice(1).some(n => isPricedYear(term[`year_${n}`]))) return null;
        if (years.length > 1) sawLaterEmptySlot = true;

        yearsInOrder.push(term.year_1);
        peaks.push(Number(term.year_1.peak));
    }

    if (!sawLaterEmptySlot) return null;
    // Successive agreement years always differ; a repeated rate is some other shape.
    if (new Set(peaks).size !== peaks.length) return null;

    const keyMonths = new Set(parsed.map(e => e.meta.months));
    const declared = (Array.isArray(availableTerms) ? availableTerms : [])
        .map(v => parseTermKey(String(v))?.months ?? null)
        .filter((n): n is number => n !== null);
    const unmatched = declared.filter(m => !keyMonths.has(m));
    if (unmatched.length !== 1) return null;

    return {
        declaredMonths: unmatched[0],
        suffix: parsed[0].meta.suffix,
        yearsInOrder,
    };
}

/** A year slot's own quoted window, as epoch ms, or null. */
const slotWindow = (slot: any): { start: number; end: number } | null => {
    const range = slot?.date_range ?? slot?.dateRange;
    const start = Date.parse(String(range?.start_date ?? range?.startDate ?? '').slice(0, 10));
    const end = Date.parse(String(range?.end_date ?? range?.endDate ?? '').slice(0, 10));
    if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) return null;
    return { start, end };
};

/**
 * The months an agreement has run by the END of its year N, taken from the document's own dates.
 *
 * ⚠️ THIS ONLY EVER REPLACES `n * 12`. The tender's stated series still wins outright, so every
 * tender that has one keeps the exact terms it produces today.
 *
 * A stepped agreement rarely steps on its own anniversary. Origin's 30-month schedule
 * (01/07/2028 → 31/12/2030) steps on 1 January, so Agreement Year 1 is the SIX months
 * 01/07/2028 - 31/12/2028 and the sub-terms are 6 and 18. Whole-year multiples called that
 * six-month year a twelve-month one and offered 12 and 24 — terms the document never quoted,
 * priced at a rate that only holds for half as long.
 *
 * ⚠️ MEASURED FROM THE TERM'S START, NOT THE YEAR'S OWN.
 *
 * Same reason removeTermYear gives: once an opening stub year is removed, year_1 begins later
 * than the contract does, and measuring from the surviving year's start would shorten every
 * sub-term. Origin's 51-month agreement is the worked example — from the term's start its years
 * end at 15 / 27 / 39 / 51, which is the tender's own series recovered without being told it.
 */
const agreementMonthsForYear = (term: Record<string, any>, n: number): number | null => {
    const start = slotWindow(term)?.start ?? slotWindow(term.year_1)?.start;
    const end = slotWindow(term[`year_${n}`])?.end;
    if (start === undefined || end === undefined) return null;
    return inclusiveMonthSpan(start, end);
};

/**
 * The same split, spelled with the agreement year's OWN number instead of year_1.
 *
 * WARNING: ONE BUG, TWO SPELLINGS. detectSplitAgreementYears only knows the first.
 *
 * Origin's HOUSE OF CARNIVORE schedule is one 48-month stepped agreement, and the parser again
 * emitted a term per agreement year — but filed each row under the year number the document
 * printed beside it rather than under year_1:
 *
 *     12_stepped  year_1  01/12/2026 - 30/11/2027   peak 10.547
 *     24_stepped  year_2  01/12/2027 - 30/11/2028   peak 11.349
 *     36_stepped  year_3  01/12/2028 - 30/11/2029   peak 12.168
 *     48_stepped  year_4  01/12/2029 - 30/11/2030   peak 13.227
 *
 * Every guard in detectSplitAgreementYears then refuses it: the years do not start at year_1,
 * and `available_terms` is [12,24,36,48], which matches the keys exactly, so nothing is missing
 * to give the split away. All four terms were dropped for having no Year 1 rate and Origin fell
 * out of the comparison entirely.
 *
 * THE WINDOWS ARE THE PROOF HERE, not available_terms. These four run back to back and never
 * overlap, which one agreement's years do and separate term options never do — a genuine
 * 12/24/36/48 offer starts all four on the SAME day. That is what makes this safe to act on.
 *
 * Every one of these must hold: two or more terms, all stepped, each pricing exactly ONE year,
 * those year numbers climbing 1, 2, 3, ... in ascending term order, every one carrying a
 * readable window, the windows consecutive, and the rates all distinct.
 */
const detectConsecutiveYearSplit = (
    contractTerms: ContractTermsMap | null | undefined,
): { suffix: string; yearsInOrder: any[] } | null => {
    if (!contractTerms || typeof contractTerms !== 'object') return null;

    const parsed = Object.entries(contractTerms)
        .map(([key, value]) => ({ key, value, meta: parseTermKey(key) }))
        .filter((e): e is { key: string; value: any; meta: { months: number; suffix: string } } =>
            e.meta !== null && !!e.value && typeof e.value === 'object')
        .sort((a, b) => a.meta.months - b.meta.months);
    if (parsed.length < 2) return null;

    const picked: { index: number; slot: any; start: number; end: number }[] = [];
    for (const { value: term } of parsed) {
        if (term.rate_type !== 'Stepped') return null;
        const priced = yearNumbersOf(term).filter(n => isPricedYear(term[`year_${n}`]));
        if (priced.length !== 1) return null;
        const slot = term[`year_${priced[0]}`];
        const window = slotWindow(slot);
        if (!window) return null;
        picked.push({ index: priced[0], slot, start: window.start, end: window.end });
    }

    // The run has to BE an agreement: it starts at its first year, climbs one at a time, and
    // each window picks up where the last left off. A day either side of the join is tolerated
    // because documents are inconsistent about whether the end date is inclusive.
    if (picked[0].index !== 1) return null;
    for (let i = 1; i < picked.length; i++) {
        if (picked[i].index !== picked[i - 1].index + 1) return null;
        const gapDays = Math.round((picked[i].start - picked[i - 1].end) / 86400000);
        if (gapDays < 0 || gapDays > 2) return null;
    }

    // Successive agreement years always differ; a repeated rate is some other shape.
    const peaks = picked.map(p => Number(p.slot.peak));
    if (new Set(peaks).size !== peaks.length) return null;

    return { suffix: parsed[0].meta.suffix, yearsInOrder: picked.map(p => p.slot) };
};

/**
 * Rebuild the single agreement a split parse destroyed, so it can be costed at all.
 *
 * The scattered `year_1` values ARE the agreement years, in ascending term order, so the real
 * term is recoverable exactly: Origin's 12/24/36/48 keys hold Agreement Years 1–4 and rebuild
 * into one 51-month term with `year_1..year_4`.
 *
 * ⚠️ LOSSY BY NATURE, and deliberately so. The parser never captured Agreement Year 5, so no
 * amount of code can restore it — the rebuilt term carries four of five years and the document
 * still needs re-parsing. This exists because pricing 12/24/36/48 correctly and flagging the
 * gap beats dropping the retailer out of four comparisons entirely, which is what happens
 * today. validateParsedOfferSnakeCase raises the re-parse warning off the same detection.
 */
export function repairSplitAgreementYears(
    contractTerms: ContractTermsMap | null | undefined,
    availableTerms: unknown,
): ContractTermsMap {
    if (!contractTerms || typeof contractTerms !== 'object') return contractTerms ?? {};

    // Two spellings of one bug — see detectConsecutiveYearSplit. The available_terms form is
    // tried first so nothing it already rescues changes behaviour.
    const split = detectSplitAgreementYears(contractTerms, availableTerms)
        ?? detectConsecutiveYearSplit(contractTerms);
    if (!split) return contractTerms;

    const { suffix, yearsInOrder } = split;
    const rebuilt: Record<string, any> = { rate_type: 'Stepped' };
    yearsInOrder.forEach((year, i) => { rebuilt[`year_${i + 1}`] = year; });

    // Keyed by the years actually recovered, NOT the declared length. Claiming 51 months on
    // four years would have expandSteppedAgreementTerms treat year_4 as a mid-agreement year
    // and silently invent a 48-month term identical to the full one. The honest span is the
    // years we hold; the missing tail is reported as a warning, not papered over.
    return { [`${yearsInOrder.length * MONTHS_IN_YEAR}${suffix}`]: rebuilt };
}

/**
 * Sub-terms derived from a single multi-year stepped agreement, merged over the original map.
 *
 * Returns the map unchanged whenever it does not apply — more than one term length quoted, the
 * single term not stepped, or fewer than two consecutively priced years. Never mutates the
 * input and never replaces a key the offer already has.
 */
export function expandSteppedAgreementTerms(
    contractTerms: ContractTermsMap | null | undefined,
    /**
     * The tender's own year lengths, when it has stated them.
     *
     * ⚠️ WITHOUT THIS THE SUB-TERMS LAND ON YEARS THE DOCUMENT HAS NO PAGE FOR.
     *
     * Origin answered a 15/27/39/51 tender with ONE 51-month agreement carrying four years.
     * Derived at whole years that became 12 / 24 / 36 — and the document's pages are 15, 27, 39
     * and 51, so not one of them matched and Origin was blank on every page while its data sat
     * there complete. The client's model ranks that agreement in each of the tender's OWN term
     * tables, not in tables of twelve-month multiples.
     *
     * Omitted, or a year the series does not name, still falls back to whole years — which is
     * what every tender without a stated series keeps doing.
     */
    series?: TenderTermSeries | null,
): ContractTermsMap {
    if (!contractTerms || typeof contractTerms !== 'object') return contractTerms ?? {};

    const parsed = Object.entries(contractTerms)
        .map(([key, value]) => ({ key, value, parsed: parseTermKey(key) }))
        .filter((e): e is { key: string; value: any; parsed: { months: number; suffix: string } } =>
            e.parsed !== null && !!e.value && typeof e.value === 'object');

    // An offer that quotes its own term options is authoritative — leave it alone.
    const distinctMonths = new Set(parsed.map(e => e.parsed.months));
    if (distinctMonths.size !== 1 || parsed.length !== 1) return contractTerms;

    const [{ key: sourceKey, value: term, parsed: { months: fullMonths, suffix } }] = parsed;
    if (term.rate_type !== 'Stepped') return contractTerms;

    const years = yearNumbersOf(term);
    const run = pricedYearRun(term, years);
    if (run < 2) return contractTerms; // nothing shorter to offer

    const expanded: ContractTermsMap = { ...contractTerms };

    // Each whole year before the last is a term in its own right, carrying AY1..AYn.
    for (let n = 1; n < run; n++) {
        const subMonths = seriesMonthsForYear(n, series)
            ?? agreementMonthsForYear(term, n)
            ?? n * MONTHS_IN_YEAR;
        if (subMonths >= fullMonths) continue;          // never restate the full term
        const subKey = `${subMonths}${suffix}`;
        if (expanded[subKey] !== undefined) continue;   // never overwrite quoted data

        const sub: Record<string, any> = { rate_type: 'Stepped' };
        for (let y = 1; y <= n; y++) sub[`year_${y}`] = term[`year_${y}`];

        // Term span runs from the first year's start to the last included year's end, when the
        // document dated its years; the month count alone is the fallback.
        const start = term.year_1?.date_range?.start_date ?? term.date_range?.start_date ?? null;
        const end = term[`year_${n}`]?.date_range?.end_date ?? null;
        if (start && end) sub.date_range = { start_date: start, end_date: end };

        expanded[subKey] = sub;
    }

    // The full agreement stays exactly as parsed, under its own key.
    expanded[sourceKey] = term;
    return expanded;
}

/**
 * Fill a term's unpriced contract years from the SAME OFFER's other terms.
 *
 * ⚠️ WHY: an unconventional term always needs one more year slot than the retailer quoted.
 *
 * contractYearNumbers adds a slot for the partial tail, so a 15-month term needs year_1..2, a
 * 27-month needs 1..3, a 39-month 1..4, a 51-month 1..5. A retailer answering a 15/27/39/51
 * request quotes 1/2/3/4 years — always exactly one short. calculationRoutes then calls
 * unpricedContractYears, finds the tail slot empty and `return`s, skipping the term; every term
 * skips, costDataJsonb stays {} and the offer stores an empty breakdown. Standard terms never
 * hit this: ceil(48/12) is 4 and four years are quoted, so the counts match.
 *
 * Measured on prod tender fa2a96e5 (PRINCE FABRICATIONS, 15/27/39/51): Engie and Origin both
 * stored `cost_data = {}` while AGL and Momentum — the two Smooth offers, which get a year_1
 * last resort — priced normally.
 *
 * ⚠️ THE RATE IS NOT INVENTED. It is the offer's own quoted rate for that contract year.
 *
 * Engie quotes year_2 = 6.67/10.655 in its 27, 39 and 51-month terms; its 15-month term is the
 * only one missing it. Origin's parse scattered one year per term (15 -> AY1, 27 -> AY2,
 * 39 -> AY3, 51 -> AY4) and every other slot is null, so the same fill reconstructs all four
 * agreement years across all four terms.
 *
 * ⚠️ ONLY WHEN THE OFFER IS UNANIMOUS.
 *
 * A year's rate is genuinely term-dependent for most retailers — a 48-month deal prices its
 * year 1 differently from a 12-month deal. Measured across prod: of 5,700 (offer, year) pairs
 * appearing in more than one term, only 1,004 agree and 4,696 disagree across 1,928 offers.
 * So a value is only adopted when every term that quotes that year quotes the SAME rate.
 * Everything else is left exactly as it is.
 *
 * TWO GUARANTEES:
 *
 *   1. An existing rate is NEVER overwritten — only a slot with no usable rate is written. So
 *      no figure that prices today can change; a term either prices as it does now, or it
 *      starts pricing where it previously produced nothing at all.
 *
 *   2. No slot beyond the term's own year count is added, and no date_range is invented. A year
 *      no term quotes stays unpriced and its term stays out of the comparison — which is right:
 *      Origin's Agreement Year 5 was never parsed, so its 51-month term must not be costed off
 *      a manufactured rate. That term needs a re-parse, not a guess.
 */

/** The rate fields a fill copies. Everything else on the slot is left alone. */
const RATE_FIELDS = ['peak', 'offpeak', 'shoulder'] as const;

const numeric = (v: unknown): number | null => {
    const n = typeof v === 'string' ? parseFloat(v) : v;
    return typeof n === 'number' && Number.isFinite(n) && n > 0 ? n : null;
};

/** A slot is fillable when it supplies neither a peak nor an offpeak rate. */
const slotLacksRates = (slot: unknown): boolean => {
    if (!slot || typeof slot !== 'object') return true;
    const s = slot as Record<string, unknown>;
    return numeric(s.peak) === null && numeric(s.offpeak) === null;
};

/** How many contract years a term spans — mirrors calculationService.contractYearNumbers. */
const yearCountForMonths = (months: number): number => {
    const years = (months || 0) / 12;
    if (!Number.isFinite(years) || years <= 0) return 1;
    return Math.floor(years) + (years % 1 > 0 ? 1 : 0);
};

export function fillContractYearsAcrossTerms(
    contractTerms: ContractTermsMap | null | undefined,
): ContractTermsMap {
    if (!contractTerms || typeof contractTerms !== 'object') return contractTerms ?? {};

    const parsed = Object.entries(contractTerms)
        .map(([key, value]) => ({ key, value, meta: parseTermKey(key) }))
        .filter((e): e is { key: string; value: any; meta: { months: number; suffix: string } } =>
            e.meta !== null && !!e.value && typeof e.value === 'object');
    if (parsed.length < 2) return contractTerms;   // nothing to borrow from

    // The offer's rate for each contract year — kept only where every term that quotes that
    // year quotes the same value.
    const byYear = new Map<number, Record<string, number> | null>();
    for (const { value: term } of parsed) {
        for (const year of yearNumbersOf(term)) {
            const slot = term[`year_${year}`];
            if (slotLacksRates(slot)) continue;

            const rates: Record<string, number> = {};
            for (const field of RATE_FIELDS) {
                const n = numeric(slot?.[field]);
                if (n !== null) rates[field] = n;
            }
            if (Object.keys(rates).length === 0) continue;

            if (!byYear.has(year)) { byYear.set(year, rates); continue; }
            const seen = byYear.get(year);
            if (seen === null) continue;                        // already known to disagree
            const same = RATE_FIELDS.every(f =>
                (seen![f] === undefined && rates[f] === undefined)
                || (seen![f] !== undefined && rates[f] !== undefined
                    && Math.abs(seen![f] - rates[f]) < 1e-6));
            if (!same) byYear.set(year, null);                  // term-dependent: do not fill
        }
    }

    let changed = false;
    const filled: ContractTermsMap = { ...contractTerms };

    for (const { key, value: term, meta: { months } } of parsed) {
        const needed = yearCountForMonths(months);
        let next: Record<string, any> | null = null;

        for (let year = 1; year <= needed; year++) {
            const slotKey = `year_${year}`;
            if (!slotLacksRates(term[slotKey])) continue;       // Guarantee 1 — never overwrite
            const rates = byYear.get(year);
            if (!rates) continue;                               // absent or term-dependent

            next = next ?? { ...term };
            // Keep whatever the slot already carries (date_range, target_kwh) and add the rates.
            next[slotKey] = { ...(term[slotKey] && typeof term[slotKey] === 'object' ? term[slotKey] : {}), ...rates };
            changed = true;
        }

        if (next) filled[key] = next;
    }

    return changed ? filled : contractTerms;
}
